import { FunctionComponent, useEffect, useState } from 'react'
import { Box, Button, CircularProgress } from '@material-ui/core'
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { makeStyles } from '@material-ui/styles'
import { toast } from 'react-toastify'
import axios from '../../utils/axios'
import colors from '../../colors'
import { SPACING } from '../../constants'

interface Subscription {
  status: string
  cancelAtPeriodEnd: boolean
  currentPeriodEnd: string
  amount: number | null
  interval: string | null
  cardBrand: string | null
  cardLast4: string | null
}

const INTERVAL_LABELS: Record<string, string> = {
  day: '/day', week: '/wk', month: '/mo', year: '/yr',
}

// Stripe's statuses, in the brewery's language rather than Stripe's.
const STATUS_COPY: Record<string, { label: string, tone: 'ok' | 'warn' | 'bad' }> = {
  active: { label: 'Active', tone: 'ok' },
  trialing: { label: 'Trial', tone: 'ok' },
  past_due: { label: 'Payment failed', tone: 'warn' },
  unpaid: { label: 'Payment failed', tone: 'bad' },
  incomplete: { label: 'Awaiting payment', tone: 'warn' },
  canceled: { label: 'Cancelled', tone: 'bad' },
}

// Loaded once at module scope, as Stripe recommends.
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null

const BillingPanel: FunctionComponent = () => {
  const styles = useStyles()
  const stripe = useStripe()
  const elements = useElements()

  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [tier, setTier] = useState<string>('core')
  const [loading, setLoading] = useState(true)
  const [editingCard, setEditingCard] = useState(false)
  const [saving, setSaving] = useState(false)

  const load = async () => {
    try {
      const res = await axios.get('/pro_subscription')
      setSubscription(res.data.subscription)
      setTier(res.data.tier)
    } catch {
      toast.error('Could not load your subscription')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const saveCard = async () => {
    if (!stripe || !elements) return
    const card = elements.getElement(CardElement)
    if (!card) return

    setSaving(true)
    // Tokenized in the browser; the server only ever sees the resulting id.
    const result = await stripe.createPaymentMethod({ type: 'card', card })

    if (result.error) {
      setSaving(false)
      toast.error(result.error.message || 'That card could not be verified.')
      return
    }

    try {
      const res = await axios.put('/pro_subscription/payment_method', {
        payment_method_id: result.paymentMethod.id,
      })
      setSubscription(res.data.subscription)
      setTier(res.data.tier)
      setEditingCard(false)
      toast.success('Payment method updated')
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Could not update your payment method')
    } finally {
      setSaving(false)
    }
  }

  const act = async (path: string, successMessage: string) => {
    setSaving(true)
    try {
      const res = await axios.post(`/pro_subscription/${path}`)
      setSubscription(res.data.subscription)
      setTier(res.data.tier)
      toast.success(successMessage)
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Something went wrong')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <Box className={styles.container}><CircularProgress /></Box>
  }

  if (!subscription) {
    return (
      <Box className={styles.container}>
        <h2 className={styles.heading}>Billing</h2>
        {tier === 'pro' ? (
          // On Pro with nothing to bill against - an org from before Pro was
          // charged for, or one whose subscription was removed in Stripe.
          <>
            <p className={styles.muted}>
              You're on the Pro plan, but we don't have a payment method on file
              for it.
            </p>
            <p className={styles.alert}>
              Nothing is being charged for Pro right now. Get in touch and we'll
              get your billing set up.
            </p>
          </>
        ) : (
          <p className={styles.muted}>
            You're on the Core plan — no monthly subscription, just a service fee
            on membership revenue.
          </p>
        )}
      </Box>
    )
  }

  const status = STATUS_COPY[subscription.status] ?? { label: subscription.status, tone: 'warn' as const }
  const price = subscription.amount != null
    ? `$${Math.round(subscription.amount / 100).toLocaleString('en-US')}${INTERVAL_LABELS[subscription.interval ?? 'month'] ?? ''}`
    : '—'
  const renewal = new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  })

  return (
    <Box className={styles.container}>
      <h2 className={styles.heading}>Billing</h2>

      <Box className={styles.card}>
        <Box className={styles.row}>
          <span className={styles.label}>Plan</span>
          <span className={styles.value}>Stitch Pro · {price}</span>
        </Box>
        <Box className={styles.row}>
          <span className={styles.label}>Status</span>
          <span className={styles[status.tone]}>{status.label}</span>
        </Box>
        <Box className={styles.row}>
          <span className={styles.label}>
            {subscription.cancelAtPeriodEnd ? 'Pro access ends' : 'Next payment'}
          </span>
          <span className={styles.value}>{renewal}</span>
        </Box>
        <Box className={styles.row}>
          <span className={styles.label}>Card</span>
          <span className={styles.value}>
            {subscription.cardLast4
              ? `${subscription.cardBrand} ···· ${subscription.cardLast4}`
              : 'None on file'}
          </span>
        </Box>

        {subscription.status === 'past_due' && (
          <p className={styles.alert}>
            Your last payment didn't go through. Updating your card below will let
            Stripe retry it.
          </p>
        )}

        {subscription.cancelAtPeriodEnd && (
          <p className={styles.alert}>
            Your subscription is set to end on {renewal}. You'll keep Pro until then.
          </p>
        )}

        {editingCard ? (
          <Box className={styles.cardEntry}>
            <CardElement options={{ style: { base: { fontSize: '16px', color: colors.STITCH_BLACK } } }} />
            <Box className={styles.actions}>
              <Button type="button" variant="outlined" disabled={saving} onClick={() => setEditingCard(false)} sx={secondarySx}>
                Cancel
              </Button>
              <Button type="button" disableElevation disabled={!stripe || saving} onClick={saveCard} sx={primarySx}>
                {saving ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : null}
                Save card
              </Button>
            </Box>
          </Box>
        ) : (
          <Box className={styles.actions}>
            <Button type="button" variant="outlined" disabled={saving} onClick={() => setEditingCard(true)} sx={secondarySx}>
              Update card
            </Button>
            {/* Resume is kept: an org whose subscription was already set to
                cancel (through support) must still be able to undo it. Self
                serve cancellation is deliberately not offered here. */}
            {subscription.cancelAtPeriodEnd && (
              <Button type="button" disableElevation disabled={saving} onClick={() => act('resume', 'Subscription resumed')} sx={primarySx}>
                Resume subscription
              </Button>
            )}
          </Box>
        )}
      </Box>

    </Box>
  )
}

const primarySx = { textTransform: 'none', fontWeight: 700, borderRadius: '8px' } as const
const secondarySx = {
  textTransform: 'none', fontWeight: 600, borderRadius: '8px',
  backgroundColor: colors.WHITE, color: colors.GREY_1, borderColor: colors.GREY_4,
} as const

const useStyles = makeStyles({
  container: { padding: SPACING * 3, maxWidth: 560 },
  heading: { margin: 0, fontSize: 22, fontWeight: 700, color: colors.STITCH_BLACK },
  muted: { fontSize: 14, color: colors.GREY_2 },
  card: {
    marginTop: SPACING * 2,
    border: `1px solid ${colors.GREY_5}`,
    borderRadius: 12,
    padding: SPACING * 3,
    background: colors.WHITE,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: `${SPACING}px 0`,
    borderBottom: `1px solid ${colors.GREY_6}`,
  },
  label: { fontSize: 13, color: colors.GREY_2 },
  value: { fontSize: 14, color: colors.STITCH_BLACK, fontWeight: 600 },
  ok: { fontSize: 14, fontWeight: 700, color: colors.GREEN },
  warn: { fontSize: 14, fontWeight: 700, color: colors.ORANGE },
  bad: { fontSize: 14, fontWeight: 700, color: colors.RED },
  alert: {
    marginTop: SPACING * 2,
    marginBottom: 0,
    padding: SPACING * 1.5,
    borderRadius: 8,
    background: colors.PALE_YELLOW,
    fontSize: 13,
    color: colors.GREY_1,
  },
  cardEntry: {
    marginTop: SPACING * 2,
    border: `1px solid ${colors.GREY_4}`,
    borderRadius: 8,
    padding: SPACING * 1.75,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: SPACING,
    marginTop: SPACING * 2,
  },
})

// The logged-in app has no Elements provider, so this page brings its own.
const Billing: FunctionComponent = () => (
  <Elements stripe={stripePromise}>
    <BillingPanel />
  </Elements>
)

export default Billing
